import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  TextField, Button, MenuItem, Container, Typography, Box, Grid, Paper,
  DialogTitle, Dialog, DialogActions, DialogContent, DialogContentText, Avatar
} from '@mui/material';
import { CircularProgress } from '@mui/material';
import HttpsIcon from '@mui/icons-material/Https';
import Swal from 'sweetalert2';
import { message } from 'antd';

const validationSchema = Yup.object({
  serviceName: Yup.string().required('Nombre del servicio es requerido'),
  servicePrice: Yup.number().required('Precio del servicio es requerido').positive('Debe ser un número positivo'),
  serviceType: Yup.string().required('Tipo de servicio es requerido'),
  status: Yup.string().required('Estado del servicio es requerido'),
});

const formatValues = (values) => {
  const service = {
    name: values.serviceName,
    description: values.serviceDescription,
    price: values.servicePrice,
    type: values.serviceType,
    status: values.status
  }
  return service;
};

function AddService() {
  const [serviceTypes, setServiceTypes] = useState([]);
  const [selectedServiceType, setSelectedServiceType] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [permissionError, setPermissionError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();

  const token = localStorage.getItem('token');

  const checkPermissions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      messageApi.error('No se encontró el token de autenticación.');
      setPermissionError(true);
      window.location.href = '/sign-in'; // Redirect to login if no token
      return;
    }
    setPermissionError(false);
    setIsLoading(true);
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/users/check-modify-role-permissions', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 403) {
        messageApi.error('No tienes permisos para acceder a esta sección.');
        setPermissionError(true);
        throw new Error('No tienes permisos para acceder a esta sección.');
      }
    } catch (error) {
      messageApi.error(error.message || 'Error al verificar permisos.');
      throw error; // Re-throw to handle it in the calling function
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  const getServiceTypes = async () => {
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/serviceTypes-list', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.error) {
        console.error(data.error);
        setServiceTypes([{ serviceType: 'Error al cargar tipos de servicio, refresca la página' }]);
      } else {
        const addServiceType = { id: 'agregar', serviceType: 'Agregar nuevo tipo de servicio' };
        setServiceTypes([...data, addServiceType]);
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.error('Error al cargar tipos de servicio');
    }
  };

  const postServiceType = async (serviceType) => {
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/serviceType-create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(serviceType)
      });
      const data = await response.json();
      if (data.error) {
        return { success: false, message: 'Ha ocurrido un error al registrar el tipo de servicio, por favor intenta de nuevo.' };
      } else {
        return { success: true, message: 'El tipo de servicio ha sido registrado exitosamente.' };
      }
    } catch (error) {
      return { success: false, message: 'Ha ocurrido un error inesperado.' };
    }
  };

  const postService = async (service) => {
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/service-create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(service)
      });
      const data = await response.json();
      if (data.error) {
        console.error(data.error);
        await Swal.fire({
          title: 'Error al registrar servicio',
          text: 'Ha ocurrido un error al registrar el servicio, por favor intenta de nuevo.',
          icon: 'error',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#f44336'
        });
        return false;
      } else {
        await Swal.fire({
          title: 'Servicio registrado',
          text: 'El servicio ha sido registrado exitosamente.',
          icon: 'success',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#4caf50'
        });
        return true;
      }
    } catch (error) {
      console.error('Error:', error);
      await Swal.fire({
        title: 'Error',
        text: 'Ha ocurrido un error inesperado.',
        icon: 'error',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#f44336'
      });
      return false;
    }
  };

  const formik = useFormik({
    initialValues: {
      serviceName: '',
      serviceDescription: '',
      servicePrice: '',
      serviceType: '',
      status: 'activo',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const service = formatValues(values);

      const confirmReq = await Swal.fire({
        title: 'Confirmar creación de servicio',
        text: '¿Estás seguro/a de que quieres registrar este servicio?',
        icon: 'question',
        showCancelButton: true,
        cancelButtonColor: '#d33',
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Registrar servicio',
        confirmButtonColor: '#4caf50'
      });
      if (confirmReq.isConfirmed) {
        const isPosted = await postService(service);
        if (isPosted) {
          formik.resetForm();
        }
      }
    },
  });

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleServiceTypeChange = (event) => {
    const value = event.target.value;
    formik.setFieldValue('serviceType', value);
    if (value === 'agregar') {
      handleOpenDialog();
    }
  };

  const handleDialogSubmit = async (event) => {
    event.preventDefault();
    const newServiceType = event.target.serviceType.value.trim();

    // Check for duplicate service type
    const isDuplicate = serviceTypes.some(type => type.serviceType.toLowerCase() === newServiceType.toLowerCase());
    if (isDuplicate) {
      Swal.fire({
        title: 'Error',
        text: 'El tipo de servicio ya existe. Por favor, ingresa un tipo de servicio diferente.',
        icon: 'error',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#f44336'
      });
      return;
    }

    const serviceType = {
      serviceType: newServiceType,
    };

    handleCloseDialog();
    const result = await postServiceType(serviceType);
    Swal.fire({
      title: result.success ? 'Tipo de servicio registrado' : 'Error al registrar tipo de servicio',
      text: result.message,
      icon: result.success ? 'success' : 'error',
      confirmButtonText: 'Entendido',
      confirmButtonColor: result.success ? '#4caf50' : '#f44336'
    });
    if (result.success) {
      getServiceTypes();
      formik.setFieldValue('serviceType', newServiceType);
    }
  };

  useEffect(() => {
    getServiceTypes();
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            {/* Icono de candado para indicar acceso restringido */}
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios con permisos de administrador.
          </Typography>
        </Box>
      </Container>
    );
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom align="center">
          Registrar servicio
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="serviceName"
                name="serviceName"
                label="Nombre del servicio"
                value={formik.values.serviceName}
                onChange={formik.handleChange}
                error={formik.touched.serviceName && Boolean(formik.errors.serviceName)}
                helperText={formik.touched.serviceName && formik.errors.serviceName}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="serviceDescription"
                name="serviceDescription"
                label="Descripción del servicio (Opcional)"
                value={formik.values.serviceDescription}
                onChange={formik.handleChange}
                error={formik.touched.serviceDescription && Boolean(formik.errors.serviceDescription)}
                helperText={formik.touched.serviceDescription && formik.errors.serviceDescription}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="servicePrice"
                name="servicePrice"
                label="Precio del servicio"
                type="number"
                value={formik.values.servicePrice}
                onChange={formik.handleChange}
                error={formik.touched.servicePrice && Boolean(formik.errors.servicePrice)}
                helperText={formik.touched.servicePrice && formik.errors.servicePrice}
                margin="normal"
              />
            </Grid>
            {(serviceTypes.length > 0) && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="serviceType"
                  name="serviceType"
                  label="Tipo de servicio"
                  select
                  value={formik.values.serviceType}
                  onChange={handleServiceTypeChange}
                  error={formik.touched.serviceType && Boolean(formik.errors.serviceType)}
                  helperText={formik.touched.serviceType && formik.errors.serviceType}
                  margin="normal"
                >
                  {serviceTypes.map((type, index) => (
                    <MenuItem key={index} value={type.id}>{type.serviceType}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="status"
                name="status"
                label="Estado del servicio"
                select
                value={formik.values.status}
                onChange={formik.handleChange}
                error={formik.touched.status && Boolean(formik.errors.status)}
                helperText={formik.touched.status && formik.errors.status}
                margin="normal"
              >
                <MenuItem value="activo">Activo</MenuItem>
                <MenuItem value="inactivo">Inactivo</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          <Button color="primary" variant="contained" fullWidth type="submit"
            sx={{
              md: 3, mt: 3,
              backgroundColor: '#f759ab', color: '#fff',
              '&:hover': {
                backgroundColor: '#eb2f96'
              },
              padding: '10px 20px', fontSize: '18px', fontWeight: 'bold', borderRadius: '8px'
            }}>
            Registrar servicio
          </Button>
        </form>
      </Paper>
      <Dialog open={openDialog} onClose={handleCloseDialog}
        PaperProps={{
          component: 'form',
          onSubmit: handleDialogSubmit
        }}
      >
        <DialogTitle>Registrar nuevo tipo de servicio</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ingresa el nombre del nuevo tipo de servicio que deseas registrar.
          </DialogContentText>
          <TextField
            autoFocus
            required
            margin="dense"
            id="serviceType"
            name="serviceType"
            label="Nombre del tipo de servicio"
            type="text"
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancelar
          </Button>
          <Button type="submit" color="primary">
            Registrar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AddService;